import { type JSX, useState } from 'react';
import { useAccount } from 'wagmi';
import Button from '@/shared/components/flip-ui-kit/Button';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import useAuction from '@/shared/hooks/useAuction';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useTracking from '@/shared/hooks/useTracking';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/large/PlusCircle';
import AuctionTable from '../../components/auction/AuctionTable';
import AuctionStatPanels from '../../components/v2/auction/AuctionStatPanels';
import { Layout } from '../../components/v2/Layout';
import { RegisterNodeModal } from '../../components/v2/operator/RegisterNodeModal';
import { getAllValidatorsQuery } from '../../graphql/cache/validatorQueries';
import { StakeEvents, type StakeTrackEvents } from '../../types/track';

export default function Home(): JSX.Element | null {
  const track = useTracking<StakeTrackEvents>();
  const { isConnected } = useAccount();
  const [addNodeModalActive, setAddNodeModalActive] = useState(false);

  const {
    auction,
    isLoading: isAuctionsLoading,
    error: auctionsError,
  } = useAuction({ pollInterval: TABLE_POLL_INTERVAL });

  const { data: validatorsData, isLoading: validatorsLoading } = useGqlQuery(
    getAllValidatorsQuery,
    {
      refetchInterval: TABLE_POLL_INTERVAL,
      context: { clientName: 'statechainCache' },
    },
  );

  const validators = validatorsData?.validators?.nodes ?? [];
  const qualifiedNodes = validators.filter((v) => v.isQualified).length;

  return (
    <Layout title="Auction">
      <div className="flex-col">
        <div className="flex w-full flex-row items-center justify-between pb-14 pt-4">
          <div className="text-35 text-white">Auctions</div>
          <Button
            size="large"
            className="hidden sm:flex"
            iconPos="left"
            icon={<PlusCircleIcon className="-ml-2 stroke-current" />}
            onClick={() => {
              track(StakeEvents.AddNode, {
                props: {
                  path: window.location.pathname,
                  position: 'table_top_right',
                },
              });
              setAddNodeModalActive(true);
            }}
            disabled={!isConnected}
          >
            Register Node
          </Button>
        </div>
        <AuctionStatPanels
          auction={auction}
          isLoading={isAuctionsLoading}
          qualifiedNodes={qualifiedNodes}
          error={auctionsError}
        />
        <AuctionTable validators={validators} isLoading={validatorsLoading} />
      </div>
      <RegisterNodeModal open={addNodeModalActive} onClose={() => setAddNodeModalActive(false)} />
    </Layout>
  );
}
